import { Suspense, lazy } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { UserProvider } from "@/contexts/UserContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TestModeBanner } from "@/components/TestModeBanner";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ScrollToTopOnNavigate } from "@/components/ScrollToTopOnNavigate";
import { HtmlRedirectHandler } from "@/components/HtmlRedirectHandler";

import { useLemonSqueezyCheckoutSuccess } from "@/hooks/useLemonSqueezyCheckoutSuccess";

import Home from "./pages/Home";
import Consultation from "./pages/Consultation";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Account from "./pages/Account";
import Auth from "./pages/Auth";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Disclaimer from "./pages/Disclaimer";
import RefundPolicy from "./pages/RefundPolicy";
import Security from "./pages/Security";
import NotFound from "./pages/NotFound";

// Lazy load heavy tool pages for better initial bundle size
const Documents = lazy(() => import("./pages/Documents"));
const PolicyPreview = lazy(() => import("./pages/PolicyPreview"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SoAGenerator = lazy(() => import("./pages/SoAGeneratorV2"));
const RiskAssessment = lazy(() => import("./pages/RiskAssessmentV2"));
const GapAnalysis = lazy(() => import("./pages/GapAnalysisV2"));
const InternalAuditChecklist = lazy(() => import("./pages/InternalAuditChecklistV2"));
const ImplementationRoadmap = lazy(() => import("./pages/ImplementationRoadmapV2"));
const ISMSScopeBuilder = lazy(() => import("./pages/ISMSScopeBuilder"));
const AssetInventory = lazy(() => import("./pages/AssetInventory"));
const AnnexASelector = lazy(() => import("./pages/AnnexASelector"));
const SupplierRiskManager = lazy(() => import("./pages/SupplierRiskManager"));
const RTORPOHelper = lazy(() => import("./pages/RTORPOHelper"));
const VendorSecurityChecker = lazy(() => import("./pages/VendorSecurityChecker"));
const Tools = lazy(() => import("./pages/Tools"));
const RiskManagement = lazy(() => import("./pages/RiskManagement"));
const RiskManagerLanding = lazy(() => import("./pages/RiskManagerLanding"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Start = lazy(() => import("./pages/Start"));
const Copilot = lazy(() => import("./pages/Copilot"));
const CheckoutLaunch = lazy(() => import("./pages/CheckoutLaunch"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const ReadinessCheck = lazy(() => import("./pages/ReadinessCheck"));


const ISO27001GapAnalysis = lazy(() => import("./pages/ISO27001GapAnalysis"));
const ISO27001Templates = lazy(() => import("./pages/ISO27001Templates"));
const AdminSubscriptions = lazy(() => import("./pages/admin/Subscriptions"));
const AdminImplementationReviews = lazy(() => import("./pages/admin/ImplementationReviews"));
const ImplementationReview = lazy(() => import("./pages/ImplementationReview"));
const AuditRoom = lazy(() => import("./pages/AuditRoom"));
const AuditRoomLanding = lazy(() => import("./pages/AuditRoomLanding"));
const AiAssistantLanding = lazy(() => import("./pages/AiAssistantLanding"));
const ReadinessCheckLanding = lazy(() => import("./pages/ReadinessCheckLanding"));
const SoAGeneratorLanding = lazy(() => import("./pages/SoAGeneratorLanding"));
const VendorCheckerLanding = lazy(() => import("./pages/VendorCheckerLanding"));
const AnnexAControlsIndex = lazy(() => import("./pages/AnnexAControlsIndex"));
const ControlDetail = lazy(() => import("./pages/ControlDetail"));
import { RMAccessGate } from "@/components/risk-management/RMAccessGate";


const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();


// Wrapper component to use hooks inside BrowserRouter
const AppContent = () => {
  useLemonSqueezyCheckoutSuccess();
  const location = useLocation();
  const isFullViewportRoute = location.pathname === "/iso-27001-ai-assistant";
  return (
    <>
      <HtmlRedirectHandler />
      <ScrollToTopOnNavigate />
      <div className={isFullViewportRoute ? "h-[100dvh] flex flex-col overflow-hidden" : "min-h-screen flex flex-col"}>
        <TestModeBanner />
        <Header />
        <main className={isFullViewportRoute ? "flex-1 min-h-0" : "flex-1"}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/documents" element={<Suspense fallback={<PageLoader />}><Documents /></Suspense>} />
            <Route path="/consultation" element={<Consultation />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/account" element={<Account />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/tools/soa-generator" element={<Suspense fallback={<PageLoader />}><SoAGenerator /></Suspense>} />
            <Route path="/tools/risk-assessment" element={<Suspense fallback={<PageLoader />}><RiskAssessment /></Suspense>} />
            <Route path="/tools/gap-analysis" element={<Suspense fallback={<PageLoader />}><GapAnalysis /></Suspense>} />
            <Route path="/tools/internal-audit" element={<Suspense fallback={<PageLoader />}><InternalAuditChecklist /></Suspense>} />
            <Route path="/tools/implementation-roadmap" element={<Suspense fallback={<PageLoader />}><ImplementationRoadmap /></Suspense>} />
            <Route path="/tools/isms-scope-builder" element={<Suspense fallback={<PageLoader />}><ISMSScopeBuilder /></Suspense>} />
            <Route path="/tools/asset-inventory" element={<Suspense fallback={<PageLoader />}><AssetInventory /></Suspense>} />
            <Route path="/tools/annex-a-selector" element={<Suspense fallback={<PageLoader />}><AnnexASelector /></Suspense>} />
            <Route path="/tools/supplier-risk-manager" element={<Suspense fallback={<PageLoader />}><SupplierRiskManager /></Suspense>} />
            <Route path="/tools/rto-rpo-bia-helper" element={<Suspense fallback={<PageLoader />}><RTORPOHelper /></Suspense>} />
            <Route path="/tools/vendor-security-checker" element={<Suspense fallback={<PageLoader />}><VendorSecurityChecker /></Suspense>} />
            <Route path="/vendor-security-checker" element={<Navigate to="/tools/vendor-security-checker" replace />} />
            <Route path="/tools" element={<Suspense fallback={<PageLoader />}><Tools /></Suspense>} />
            <Route path="/gap-analysis" element={<Navigate to="/iso-27001-gap-analysis" replace />} />
            <Route path="/iso-27001-gap-analysis-tool" element={<Navigate to="/iso-27001-gap-analysis" replace />} />
            <Route path="/iso-27001-document-summary" element={<Navigate to="/iso-27001-templates" replace />} />
            <Route path="/blog/iso-27001-vs-soc2" element={<Navigate to="/blog/iso-27001-vs-soc-2" replace />} />
            <Route path="/blog/iso-27001-vs-soc-2-comparison" element={<Navigate to="/blog/iso-27001-vs-soc-2" replace />} />
            <Route path="/blog/soc-2-vs-iso-27001-comparison-2026" element={<Navigate to="/blog/iso-27001-vs-soc-2" replace />} />
            <Route path="/risk-management" element={<Suspense fallback={<PageLoader />}><RiskManagerLanding /></Suspense>} />
            <Route path="/start" element={<Suspense fallback={<PageLoader />}><Start /></Suspense>} />
            <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><Pricing /></Suspense>} />
            <Route path="/enterprise" element={<Suspense fallback={<PageLoader />}><Enterprise /></Suspense>} />
            <Route path="/iso-27001-readiness-check" element={<Suspense fallback={<PageLoader />}><ReadinessCheck /></Suspense>} />
            <Route path="/iso-27001-ai-assistant" element={<Suspense fallback={<PageLoader />}><Copilot /></Suspense>} />
            <Route path="/checkout-launch" element={<Suspense fallback={<PageLoader />}><CheckoutLaunch /></Suspense>} />
            <Route path="/iso27001-copilot" element={<Navigate to="/iso-27001-ai-assistant" replace />} />
            <Route path="/copilot" element={<Navigate to="/iso-27001-ai-assistant" replace />} />
            
            
            <Route path="/iso-27001-gap-analysis" element={<Suspense fallback={<PageLoader />}><ISO27001GapAnalysis /></Suspense>} />
            <Route path="/iso-27001-templates" element={<Suspense fallback={<PageLoader />}><ISO27001Templates /></Suspense>} />
            <Route path="/risk-copilot/demo/*" element={<Suspense fallback={<PageLoader />}><RiskManagement /></Suspense>} />
            <Route path="/risk-copilot/*" element={<Suspense fallback={<PageLoader />}><RMAccessGate><RiskManagement /></RMAccessGate></Suspense>} />
            <Route path="/iso-27001-audit-room" element={<Suspense fallback={<PageLoader />}><AuditRoom /></Suspense>} />
            <Route path="/audit-room" element={<Suspense fallback={<PageLoader />}><AuditRoomLanding /></Suspense>} />
            <Route path="/ai-assistant" element={<Suspense fallback={<PageLoader />}><AiAssistantLanding /></Suspense>} />
            <Route path="/readiness-check" element={<Suspense fallback={<PageLoader />}><ReadinessCheckLanding /></Suspense>} />
            <Route path="/soa-generator" element={<Suspense fallback={<PageLoader />}><SoAGeneratorLanding /></Suspense>} />
            <Route path="/vendor-checker" element={<Suspense fallback={<PageLoader />}><VendorCheckerLanding /></Suspense>} />
            <Route path="/admin/subscriptions" element={<Suspense fallback={<PageLoader />}><AdminSubscriptions /></Suspense>} />
            <Route path="/admin/implementation-reviews" element={<Suspense fallback={<PageLoader />}><AdminImplementationReviews /></Suspense>} />
            <Route path="/iso-27001-implementation-review" element={<Suspense fallback={<PageLoader />}><ImplementationReview /></Suspense>} />
            <Route path="/iso-27001-annex-a-controls" element={<Suspense fallback={<PageLoader />}><AnnexAControlsIndex /></Suspense>} />
            <Route path="/annex-a" element={<Navigate to="/iso-27001-annex-a-controls" replace />} />
            <Route path="/controls/:slug" element={<Suspense fallback={<PageLoader />}><ControlDetail /></Suspense>} />
            
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/security" element={<Security />} />
            <Route path="/blog" element={<Suspense fallback={<PageLoader />}><Blog /></Suspense>} />
            <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPost /></Suspense>} />
            <Route path="/:slug" element={<Suspense fallback={<PageLoader />}><PolicyPreview /></Suspense>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        {!isFullViewportRoute && <Footer />}
        <ScrollToTop />
      </div>
    </>
  );
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem={true} storageKey="iso27001kit-theme">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <LanguageProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </LanguageProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
